import type { CardRow, CardSpec } from '@itc/cards';
import { shortAUEC } from '@itc-mod/bank/shared';
import { fmtSCU } from '@itc-mod/supply/shared';
import { lineText, linesSCU } from './amounts.ts';
import { publicDonors, totalsText, type BoardEntry, type BoardPeriod } from './boards.ts';
import { STAGE, haulerOf, incomingOf, placeName, requestAmount, type IncomingText, type TextNames } from './discord-text.ts';
import { activeByUrgency, type LineProgress, type Request, type RequestView } from './requests.ts';

// Donations' cards (plan 2E R10, after `imgSpec`, lines 1628–1654). Cards leave the app,
// so opted-out donors are left out here, for every viewer: a receiver's board read still
// holds them, marked. Guests named Anonymous never reach a board at all.

const period = (value: BoardPeriod) => (value === 'all' ? 'All time' : `Last ${value} days`);

export function donorsCard(donors: readonly BoardEntry[], chosen: BoardPeriod): CardSpec {
  const shown = publicDonors(donors);
  return {
    kicker: 'Sierra Squadron · Thank you',
    title: 'Thank you to our donors',
    sub: period(chosen),
    stats: [
      { label: 'Donors', value: shown.length },
      { label: 'Donations', value: shown.reduce((sum, donor) => sum + donor.count, 0) },
    ],
    rows: shown.map((donor) => ({ left: donor.name, right: totalsText(donor, 2) })),
  };
}

function lineOf(request: Pick<Request, 'lines'>, entry: LineProgress) {
  return request.lines.find((line) => line.id === entry.lineId);
}

export function requestCard(view: RequestView, input: { thanks: readonly string[]; requester: string; deliverTo: string }): CardSpec {
  const { request, progress } = view;
  const scu = progress.flatMap((entry) => {
    const line = lineOf(request, entry);
    return line?.unit === 'scu' ? [{ got: entry.got, wanted: line.quantity }] : [];
  });
  const got = scu.reduce((sum, entry) => sum + entry.got, 0);
  const wanted = scu.reduce((sum, entry) => sum + entry.wanted, 0);
  const rows: CardRow[] = progress.flatMap((entry) => {
    const line = lineOf(request, entry);
    if (line === undefined) return [];
    const pledged = entry.pledged > 0 ? ` · +${requestAmount(entry.pledged, line.unit)} pledged` : '';
    return [{ left: line.name, right: `${requestAmount(entry.got, line.unit)} / ${requestAmount(line.quantity, line.unit)}${pledged}`, tone: entry.left > 0 ? undefined : 'g' }];
  });
  return {
    kicker: 'Sierra Squadron · Donations needed',
    title: request.title,
    sub: [input.deliverTo === '' ? '' : `Drop-off ${input.deliverTo}`, request.neededBy === null ? '' : `Needed by ${new Date(request.neededBy).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`, input.requester === '' ? '' : `Contact ${input.requester}`].filter((part) => part !== '').join(' · '),
    stats: [
      { label: 'Progress', value: wanted > 0 ? `${String(Math.round((got / wanted) * 100))}%` : null, tone: got >= wanted ? 'g' : 'a' },
      { label: 'Priority', value: request.priority === 'Normal' ? null : request.priority, tone: 'c' },
      { label: 'Donors', value: input.thanks.length > 0 ? input.thanks.length : null },
    ],
    rows,
    chips: input.thanks.length > 0 ? { label: 'Thanks to', names: input.thanks } : null,
    note: 'Reply in Discord or DM the contact to donate.',
  };
}

function stillNeeded(view: RequestView): string {
  const open = view.progress.flatMap((entry) => {
    const line = lineOf(view.request, entry);
    return line === undefined || entry.open <= 0 ? [] : [`${line.name} ${requestAmount(entry.open, line.unit)}`];
  });
  return open.length > 0 ? `needs ${open.join(', ')}` : 'covered';
}

export function requestsCard(views: readonly RequestView[], donors30: number): CardSpec {
  const active = activeByUrgency(views.map((view) => view.request)).flatMap((request) => views.filter((view) => view.request.id === request.id));
  return {
    kicker: 'Sierra Squadron · Donations needed',
    title: 'Open donation requests',
    sub: 'What Sierra is collecting right now',
    stats: [
      { label: 'Open requests', value: active.length },
      { label: 'Donors, 30 days', value: donors30 },
    ],
    rows: active.map((view) => {
      const wanted = view.progress.reduce((sum, entry) => sum + (lineOf(view.request, entry)?.quantity ?? 0), 0);
      const got = view.progress.reduce((sum, entry) => sum + entry.got, 0);
      return { left: view.request.title, right: `${wanted > 0 ? `${String(Math.round((got / wanted) * 100))}% · ` : ''}${stillNeeded(view)}`, tone: view.request.priority === 'Urgent' || view.request.priority === 'High' ? 'c' : undefined };
    }),
    note: active.length > 0 ? 'Reply in Discord or DM a Sierra leader to donate.' : 'Nothing open right now. Thank you, everyone.',
  };
}

export function pickupCard(entry: IncomingText, names: TextNames): CardSpec {
  const card = incomingOf(entry);
  const when = card.pickup?.at ?? null;
  const load = linesSCU(card.lines);
  const collector = card.pickup?.collector == null ? '' : (names.people[card.pickup.collector] ?? '');
  const to = placeName(card.place, names);
  return {
    kicker: `Sierra Squadron · ${card.kind} pickup`,
    title: `Pickup from ${card.title}`,
    sub: [card.pickup?.from ? `Collect at ${card.pickup.from}` : '', when === null ? '' : new Date(when).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })].filter((part) => part !== '').join(' · ') || 'Time to be arranged',
    stats: [
      { label: 'Status', value: STAGE[card.pickup?.stage ?? 'pledged'] },
      { label: 'Collector', value: collector },
      { label: 'Hauling ship', value: haulerOf(card.pickup, names) },
      { label: 'Load', value: load > 0 ? `${fmtSCU(load)} SCU` : '' },
    ],
    rows: card.lines.filter((line) => line.quantity > 0).map((line) => ({ left: line.name, right: lineText(line).replace(` ${line.name}`, '') })),
    note: [to === '' ? '' : `Deliver to ${to}`, card.pickup?.plan ?? ''].filter((part) => part !== '').join(' · '),
  };
}

const aUECShort = (amount: number) => `${shortAUEC(amount)} aUEC`;

/** One bank donor, as `GET /donations/bank-donors` gives them. */
export interface BankDonorRow {
  name: string;
  total: number;
  count: number;
}

export function bankDonorsCard(donors: readonly BankDonorRow[], chosen: BoardPeriod): CardSpec {
  return {
    kicker: 'Sierra Squadron · Corp bank',
    title: 'Thank you to our bank donors',
    sub: period(chosen),
    stats: [
      { label: 'Donors', value: donors.length },
      { label: 'Donated', value: aUECShort(donors.reduce((sum, donor) => sum + donor.total, 0)), tone: 'g' },
    ],
    rows: donors.map((donor, index) => ({ left: `${String(index + 1)}. ${donor.name}`, right: `${aUECShort(donor.total)}${donor.count > 1 ? ` · ${String(donor.count)}×` : ''}`, tone: index < 3 ? 'a' : undefined })),
    note: donors.length > 0 ? '' : 'No donations to the bank in this period.',
  };
}

